import { countryOptions } from '@nxsys/constants/countries';
import { includes, filter, isNumber } from 'lodash';
import moment from 'moment';

const upperCaseFirstLetter = (string: string) => {
  if (!string) return '';
  return string.charAt(0).toUpperCase() + string.slice(1);
};

export const convertObjectKeysToString = (key: string) => {
  if (!key) return '';
  const keyParse = key.replace('otp', 'opt');
  const arrayString = keyParse?.split(/(?=[A-Z])/) || [];
  const mappedArray =
    arrayString?.map((key) => upperCaseFirstLetter(key)) || [];
  const joinedString = mappedArray?.join(' ') || '';
  return joinedString;
};

export const renderString = (value: any, label: string) => {
  if (typeof value === 'string') {
    if (value === 'TRUE' || value === 'FALSE') {
      return value === 'TRUE' ? 'Yes' : 'No';
    } else if (value && moment(value).isValid()) {
      return includes(
        [
          'createdAt',
          'updatedAt',
          'startDate',
          'endDate',
          'deletedAt',
          'DOB',
          'verificationDate',
          'matchDate',
          'incorporationDate',
          'VATEffectiveDate',
          'cisVerificationDate',
          'receivedOn',
          'documentDate',
          'expiryDate',
          'importOn',
          'issueDate',
          'billDate',
          'invoicedDate',
          'dueDate',
          'datePaid',
          'paymentDate',
          'dateOfJoining',
          'dateLeft',
          'creditDate',
          'documentExpiryDate',
          'assessedAt',
          'ejhDate',
          'postponementEndDate',
          'membershipStartDate',
          'refundMadeDate',
          'dateOnClassifiedOn',
          'lastAssessmentOn',
          'otpInDate',
          'otpOutDate',
          'lastAssessmentDoneOn',
          'dateOnWhichClassifiedOn',
        ],
        label,
      )
        ? moment(value)?.format('DD-MM-YYYY')
        : includes(['sendEmailP60At'], label)
          ? moment(value)?.format('MMMM, DD')
          : value;
    } else if (includes(['country', 'emergencyCountry'], label) && value) {
      const country = filter(countryOptions, (item) => item?.value === value)
        ?.length
        ? filter(countryOptions, (item) => item?.value === value)[0]
        : '';
      return country ? country?.label : '';
    }
    return value;
  } else if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  } else {
    return value?.length && Array.isArray(value) && typeof value[0] === 'string'
      ? value?.filter((item) => item)?.toString()
      : isNumber(value) && !includes(['memberId'], label)
        ? value.toFixed(2)
        : typeof value === 'object'
          ? null
          : value?.toString() || null;
  }
};

function getAllPaths(obj: any, path = ''): string[] {
  const paths: string[] = [];

  for (const key in obj) {
    const fullPath = path ? `${path}.${key}` : key;

    if (typeof obj[key] === 'object' && obj[key] !== null) {
      if (Array.isArray(obj[key])) {
        obj[key].forEach((_, index) => {
          paths.push(...getAllPaths(obj[key][index], `${fullPath}[${index}]`));
        });
      } else {
        paths.push(...getAllPaths(obj[key], fullPath));
      }
    } else {
      paths.push(fullPath);
    }
  }

  return paths;
}

export const compareObjects = (obj1: any, obj2: any, path = ''): string[] => {
  const diffs: string[] = [];

  // Handle cases where one of the objects is null
  if (obj1 === null && obj2 !== null) {
    diffs.push(...getAllPaths(obj2, path));
    return diffs;
  } else if (obj2 === null && obj1 !== null) {
    diffs.push(...getAllPaths(obj1, path));
    return diffs;
  } else if (obj1 === null && obj2 === null) {
    return diffs; // Both are null, no difference
  }

  // Helper to add a difference with full path to the key
  const addDiff = (keyPath: string) => diffs.push(keyPath);

  // Compare each key in obj1
  for (const key in obj1) {
    const fullPath = path ? `${path}.${key}` : key;

    if (!Object.prototype.hasOwnProperty.call(obj2, key)) {
      addDiff(fullPath); // key missing in obj2
    } else if (typeof obj1[key] === 'object' && obj1[key] !== null) {
      if (Array.isArray(obj1[key]) && Array.isArray(obj2[key])) {
        // Compare arrays
        if (obj1[key].length !== obj2[key].length) {
          addDiff(fullPath); // different array lengths
        } else {
          obj1[key].forEach((item, index) => {
            diffs.push(
              ...compareObjects(
                item,
                obj2[key][index],
                `${fullPath}[${index}]`,
              ),
            );
          });
        }
      } else if (
        !Array.isArray(obj1[key]) &&
        typeof obj2[key] === 'object' &&
        obj2[key] !== null
      ) {
        // Recursively compare objects
        diffs.push(...compareObjects(obj1[key], obj2[key], fullPath));
      } else {
        addDiff(fullPath); // type mismatch
      }
    } else if (obj1[key] !== obj2[key]) {
      addDiff(fullPath); // primitive value mismatch
    }
  }

  // Check for keys in obj2 that are not in obj1
  for (const key in obj2) {
    const fullPath = path ? `${path}.${key}` : key;
    if (!Object.prototype.hasOwnProperty.call(obj1, key)) {
      addDiff(fullPath);
    }
  }

  return diffs;
};

export const getLabelByObjectPath = (path: string) => {
  switch (path) {
    case 'rpns[0].employeeID.employeePpsn':
      return 'Employee PPSN';
    case 'rpns[0].employeeID.employmentID':
      return 'Employment ID';
    case 'rpns[0].name.firstName':
      return 'First Name';
    case 'rpns[0].name.familyName':
      return 'Family Name';
    case 'rpns[0].rpnIssueDate':
      return 'RPN Issue Date';
    case 'rpns[0].effectiveDate':
      return 'Effective Date';
    case 'rpns[0].endDate':
      return 'End Date';
    case 'rpns[0].incomeTaxCalculationBasis':
      return 'Income Tax Calculation Basis';
    case 'rpns[0].exclusionOrder':
      return 'Exclusion Order';
    case 'rpns[0].yearlyTaxCredits':
      return 'Yearly Tax Credits';
    case 'rpns[0].taxRates[0].yearlyRateCutOff':
      return 'Annual Cut-off 20%';
    case 'rpns[0].taxRates[1].yearlyRateCutOff':
      return 'Annual Cut-off 40%';
    case 'rpns[0].uscStatus':
      return 'USC Status';
    case 'rpns[0].uscRates[0].yearlyUSCRateCutOff':
      return 'Annual Cut-off 0.5%';
    case 'rpns[0].uscRates[1].yearlyUSCRateCutOff':
      return 'Annual Cut-off 2%';
    case 'rpns[0].uscRates[2].yearlyUSCRateCutOff':
      return 'Annual Cut-off 3%';
    case 'rpns[0].payForIncomeTaxToDate':
      return 'Pay For Income Tax To Date';
    case 'rpns[0].incomeTaxDeductedToDate':
      return 'Income Tax Deducted To Date';
    case 'rpns[0].payForUSCToDate':
      return 'Pay For USC To Date';
    case 'rpns[0].uscDeductedToDate':
      return 'USC Deducted To Date';
    case 'rpns[0].lptToDeduct':
      return 'LPT';
    case 'employerName':
      return 'Employer Name';
    case 'employerRegistrationNumber':
      return 'Employer Registration Number';
    case 'taxYear':
      return 'Tax Year';
    case 'totalRPNCount':
      return 'Total RPN Count';
    case 'dateTimeEffective':
      return 'Date Effective';
    case 'rpnNumber':
      return 'RPN Number';
    default:
      return path;
  }
};
